import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { invalidateEventQueries, optimisticAddEvent } from '../event-cache';
import { allDayExclusiveEnd, dateToInput, inputToDate } from '../event-day';
import type { Calendar, Preferences } from '../types';
import Popover, { type AnchorRect } from './Popover';

interface Props {
  anchor: AnchorRect;
  calendars: Calendar[];
  initialStart: Date;
  initialEnd: Date;
  initialAllDay: boolean;
  onClose: () => void;
}

function pickDefaultCalendar(writable: Calendar[], defaultId: string | null): Calendar | null {
  if (writable.length === 0) return null;
  if (defaultId) {
    const match = writable.find((c) => c.id === defaultId);
    if (match) return match;
  }
  return writable.find((c) => c.is_primary) ?? writable[0];
}

export default function EventCreatePopover({
  anchor,
  calendars,
  initialStart,
  initialEnd,
  initialAllDay,
  onClose
}: Props) {
  const qc = useQueryClient();
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.get<Preferences>('/api/preferences')
  });
  const writable = useMemo(
    () => calendars.filter((c) => c.access_role === 'owner' || c.access_role === 'writer'),
    [calendars]
  );
  const defaultCal = pickDefaultCalendar(writable, prefsQ.data?.default_calendar_id ?? null);

  const [calendarId, setCalendarId] = useState<string>(defaultCal?.id ?? '');
  const [summary, setSummary] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [allDay, setAllDay] = useState(initialAllDay);
  const [start, setStart] = useState(dateToInput(initialStart, initialAllDay));
  const [end, setEnd] = useState(dateToInput(initialEnd, initialAllDay));
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const startDate = inputToDate(start, allDay);
      const endDate = inputToDate(end, allDay);
      if (!calendarId) throw new Error('pick a calendar');
      if (!summary.trim()) throw new Error('title required');
      if (!startDate || !endDate) throw new Error('start and end required');
      // The END input is inclusive (last day covered); Google's all-day end
      // is exclusive.
      const finalEnd = allDay ? allDayExclusiveEnd(startDate, endDate) : endDate;
      return api.post<{ id: string }>('/api/events', {
        calendar_id: calendarId,
        summary: summary.trim(),
        location: location.trim() || null,
        description: description.trim() || null,
        all_day: allDay,
        start_time: startDate.toISOString(),
        end_time: finalEnd.toISOString()
      });
    },
    onSettled: () => {
      invalidateEventQueries(qc);
    },
    onError: (err: Error) => setError(err.message)
  });

  function submit() {
    let startDate: Date | null;
    let endDate: Date | null;
    try {
      startDate = inputToDate(start, allDay);
      endDate = inputToDate(end, allDay);
      if (!calendarId) throw new Error('pick a calendar');
      if (!summary.trim()) throw new Error('title required');
      if (!startDate || !endDate) throw new Error('start and end required');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'invalid input');
      return;
    }
    const cal = calendars.find((c) => c.id === calendarId) ?? null;
    const finalEnd = allDay ? allDayExclusiveEnd(startDate, endDate) : endDate;
    optimisticAddEvent(qc, {
      id: crypto.randomUUID(),
      calendar_id: calendarId,
      summary: summary.trim(),
      location: location.trim() || null,
      start_time: startDate.toISOString(),
      end_time: finalEnd.toISOString(),
      all_day: allDay,
      status: 'confirmed',
      html_link: null,
      is_recurring_instance: false,
      countdown: null,
      background_color: cal?.background_color ?? null,
      foreground_color: cal?.foreground_color ?? null
    });
    create.mutate();
    onClose();
  }

  function toggleAllDay(next: boolean) {
    setAllDay(next);
    if (next) {
      setStart((s) => s.slice(0, 10));
      setEnd((s) => s.slice(0, 10));
    } else {
      setStart((s) => (s.length === 10 ? `${s}T09:00` : s));
      setEnd((s) => (s.length === 10 ? `${s}T10:00` : s));
    }
  }

  return (
    <Popover anchor={anchor} onClose={onClose} width={380}>
      <header className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <div className="text-sm font-semibold text-[var(--text-muted)]">New event</div>
        <button
          onClick={onClose}
          aria-label="close"
          className="rounded px-2 py-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {writable.length === 0 ? (
          <div className="text-sm text-[var(--text-faint)]">
            No writable calendars connected.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Field label="Calendar">
              <select
                value={calendarId}
                onChange={(ev) => setCalendarId(ev.target.value)}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              >
                {writable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.summary} — {c.account_email}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Title">
              <input
                type="text"
                autoFocus
                value={summary}
                onChange={(ev) => setSummary(ev.target.value)}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(ev) => toggleAllDay(ev.target.checked)}
              />
              All-day
            </label>
            <Field label="Starts">
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={start}
                onChange={(ev) => setStart(ev.target.value)}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              />
            </Field>
            <Field label="Ends">
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={end}
                onChange={(ev) => setEnd(ev.target.value)}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                value={location}
                onChange={(ev) => setLocation(ev.target.value)}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              />
            </Field>
            <Field label="Description">
              <textarea
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                rows={3}
                className="rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-[6px] text-sm"
              />
            </Field>
            {error && (
              <div className="rounded bg-[var(--today-bg)] px-3 py-2 text-[0.78rem] text-[var(--today-fg)]">
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {writable.length > 0 && (
        <footer className="flex justify-end gap-2 border-t border-[var(--border)] px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-[6px] text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="rounded-md bg-[var(--accent)] px-3 py-[6px] text-[0.8rem] text-[var(--accent-fg)] hover:opacity-90"
          >
            Create
          </button>
        </footer>
      )}
    </Popover>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
        {label}
      </span>
      {children}
    </label>
  );
}
